import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Clock, 
  Play, 
  Pause, 
  Settings, 
  Activity,
  AlertTriangle,
  CheckCircle,
  PlayCircle,
  History,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

const SchedulerManager = () => {
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerHistory, setSchedulerHistory] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Configuration state
  const [showConfig, setShowConfig] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [configForm, setConfigForm] = useState({
    enabled: true,
    hour: '0',
    minute: '0',
    timezone: 'UTC'
  });

  // Load scheduler status and history
  const loadSchedulerData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Load status and history in parallel
      const [statusResponse, historyResponse] = await Promise.all([
        api.get(endpoints.cursor.schedulerStatus),
        api.get(endpoints.cursor.schedulerHistory)
      ]);
      
      setSchedulerStatus(statusResponse.data.status);
      setSchedulerHistory(historyResponse.data.imports || []);
      
      // Update config form with current settings
      if (statusResponse.data.status) {
        const status = statusResponse.data.status;
        const cronParts = status.schedule.split(' ');
        setConfigForm({
          enabled: status.enabled,
          hour: cronParts[1] || '0',
          minute: cronParts[0] || '0',
          timezone: status.timezone || 'UTC'
        });
      }
      
    } catch (error) {
      console.error('Failed to load scheduler data:', error);
      toast.error('Failed to load scheduler information');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Load timezones for dropdown
  const loadTimezones = useCallback(async () => {
    try {
      const response = await api.get(endpoints.cursor.schedulerTimezones);
      setTimezones(response.data.timezones || []);
    } catch (error) {
      console.error('Failed to load timezones:', error);
      toast.error('Failed to load timezone options');
    }
  }, []);

  // Handle configuration form submission
  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setConfigLoading(true);
      
      // Build cron expression from hour and minute
      const cronExpression = `${configForm.minute} ${configForm.hour} * * *`;
      
      const configData = {
        schedule: cronExpression,
        timezone: configForm.timezone,
        enabled: configForm.enabled
      };
      
      await api.put(endpoints.cursor.schedulerConfig, configData);
      
      toast.success('Scheduler configuration updated successfully!');
      setShowConfig(false);
      
      // Refresh data to show updated settings
      await loadSchedulerData();
      
    } catch (error) {
      console.error('Failed to update scheduler config:', error);
      toast.error(error.response?.data?.message || 'Failed to update scheduler configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  // Handle form field changes
  const handleConfigChange = (field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    loadSchedulerData();
    loadTimezones();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadSchedulerData, 30000);
    return () => clearInterval(interval);
  }, [loadSchedulerData, loadTimezones]);

  const handleTriggerManualRun = async () => {
    try {
      setTriggering(true);
      
      await api.post(endpoints.cursor.schedulerTrigger);
      
      toast.success('Scheduled import triggered successfully!');
      
      // Refresh data after a short delay
      setTimeout(() => {
        loadSchedulerData();
      }, 2000);
      
    } catch (error) {
      console.error('Failed to trigger scheduler:', error);
      toast.error('Failed to trigger scheduled import');
    } finally {
      setTriggering(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return <AlertTriangle className="w-5 h-5 text-gray-400" />;
    
    if (status.enabled && status.running) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (status.enabled && !status.running) {
      return <Clock className="w-5 h-5 text-yellow-500" />;
    } else {
      return <Pause className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    
    if (status.enabled && status.running) {
      return 'Running';
    } else if (status.enabled && !status.running) {
      return 'Scheduled';
    } else {
      return 'Disabled';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-500';
    
    if (status.enabled && status.running) {
      return 'text-green-600';
    } else if (status.enabled && !status.running) {
      return 'text-yellow-600';
    } else {
      return 'text-red-600';
    }
  };

  const formatCronSchedule = (cronPattern) => {
    // Convert common cron patterns to human readable
    const patterns = {
      '0 0 * * *': 'Daily at midnight',
      '0 1 * * *': 'Daily at 1:00 AM',
      '0 2 * * *': 'Daily at 2:00 AM',
      '0 6 * * *': 'Daily at 6:00 AM',
      '0 0 * * 1': 'Weekly on Monday at midnight',
      '0 0 1 * *': 'Monthly on the 1st at midnight'
    };
    
    return patterns[cronPattern] || cronPattern;
  };

  const getImportStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Failed
          </span>
        );
      case 'running':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Activity className="w-3 h-3 mr-1" />
            Running
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduler Management</h1>
          <p className="text-gray-600">Automated daily imports from Cursor API</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <Settings className="w-4 h-4 mr-2" />
            {showConfig ? 'Hide Config' : 'Configure'}
          </button>
          <button
            onClick={loadSchedulerData}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Scheduler Status Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-gray-400" />
            Scheduler Status
          </h2>
        </div>
        <div className="p-6">
          {schedulerStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Status */}
              <div className="flex items-center space-x-3">
                {getStatusIcon(schedulerStatus)}
                <div>
                  <div className="text-sm font-medium text-gray-500">Status</div>
                  <div className={`text-lg font-semibold ${getStatusColor(schedulerStatus)}`}>
                    {getStatusText(schedulerStatus)}
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-500">Schedule</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCronSchedule(schedulerStatus.schedule)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {schedulerStatus.timezone}
                  </div>
                </div>
              </div>

              {/* Next Run */}
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-500">Next Run</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {schedulerStatus.nextRun ? (
                      <>
                        <div>{format(new Date(schedulerStatus.nextRun), 'MMM dd, yyyy')}</div>
                        <div className="text-sm text-gray-500">
                          {format(new Date(schedulerStatus.nextRun), 'HH:mm')} {schedulerStatus.timezone}
                        </div>
                      </>
                    ) : (
                      schedulerStatus.enabled ? 
                        'Calculating next run...' : 
                        'Scheduler disabled'
                    )}
                  </div>
                </div>
              </div>

              {/* Last Run */}
              <div className="flex items-center space-x-3">
                <History className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-500">Last Run</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {schedulerStatus.lastRun ? (
                      <>
                        <div>{format(new Date(schedulerStatus.lastRun), 'MMM dd, yyyy')}</div>
                        <div className="text-sm text-gray-500">
                          {format(new Date(schedulerStatus.lastRun), 'HH:mm')}
                        </div>
                      </>
                    ) : (
                      'Never'
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Scheduler Status Unavailable</h3>
              <p className="text-gray-500">Unable to load scheduler information</p>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Form */}
      {showConfig && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-gray-400" />
              Scheduler Configuration
            </h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleConfigSubmit} className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Enable Scheduler</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Turn the automated daily imports on or off
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={configForm.enabled}
                    onChange={(e) => handleConfigChange('enabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hour (24-hour format)
                  </label>
                  <select
                    value={configForm.hour}
                    onChange={(e) => handleConfigChange('hour', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i.toString()}>
                        {i.toString().padStart(2, '0')}:00 ({i === 0 ? 'Midnight' : i === 12 ? 'Noon' : i < 12 ? `${i} AM` : `${i - 12} PM`})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minute
                  </label>
                  <select
                    value={configForm.minute}
                    onChange={(e) => handleConfigChange('minute', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i.toString()}>
                        :{i.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Timezone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={configForm.timezone}
                  onChange={(e) => handleConfigChange('timezone', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                <p className="text-sm text-gray-600">
                  {configForm.enabled ? (
                    <>
                      Scheduler will run <strong>daily at {configForm.hour.padStart(2, '0')}:{configForm.minute.padStart(2, '0')}</strong> in <strong>{configForm.timezone}</strong> timezone
                    </>
                  ) : (
                    <span className="text-red-600">Scheduler is disabled</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cron expression: {configForm.minute} {configForm.hour} * * *
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowConfig(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={configLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {configLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Update Schedule
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Trigger Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <PlayCircle className="w-5 h-5 mr-2 text-gray-400" />
            Manual Controls
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Trigger Import Now</h3>
              <p className="text-sm text-gray-500 mt-1">
                Manually trigger an import for yesterday's data (same as scheduled import)
              </p>
            </div>
            <button
              onClick={handleTriggerManualRun}
              disabled={triggering || (schedulerStatus && schedulerStatus.running)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {triggering ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Triggering...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Trigger Now
                </>
              )}
            </button>
          </div>
          
          {schedulerStatus && schedulerStatus.running && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <Activity className="w-5 h-5 text-yellow-400 mr-2" />
                <div className="text-sm text-yellow-700">
                  Import is currently running. Please wait for it to complete before triggering another.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import History Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <History className="w-5 h-5 mr-2 text-gray-400" />
            Recent Scheduled Imports
          </h2>
        </div>
        <div className="overflow-hidden">
          {schedulerHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Range
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Events
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Import Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schedulerHistory.map((importRecord, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {importRecord.StartDate && importRecord.EndDate ? (
                          <div>
                            <div>{format(new Date(importRecord.StartDate), 'MMM dd, yyyy')}</div>
                            {importRecord.StartDate !== importRecord.EndDate && (
                              <div className="text-xs text-gray-500">
                                to {format(new Date(importRecord.EndDate), 'MMM dd, yyyy')}
                              </div>
                            )}
                          </div>
                        ) : (
                          'Unknown'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getImportStatusBadge(importRecord.Status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(importRecord.ProcessedRecords || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(importRecord.TotalRecords || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {importRecord.StartedAt ? (
                          <div>
                            <div>{format(new Date(importRecord.StartedAt), 'MMM dd, yyyy')}</div>
                            <div className="text-xs">
                              {format(new Date(importRecord.StartedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        ) : (
                          'Unknown'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {importRecord.StartedAt && importRecord.CompletedAt ? (
                          (() => {
                            const start = new Date(importRecord.StartedAt);
                            const end = new Date(importRecord.CompletedAt);
                            const durationMs = end - start;
                            const minutes = Math.floor(durationMs / 60000);
                            const seconds = Math.floor((durationMs % 60000) / 1000);
                            return `${minutes}m ${seconds}s`;
                          })()
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Import History</h3>
              <p className="text-gray-500">Scheduled imports will appear here once they start running</p>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Settings className="w-5 h-5 text-blue-400 mr-2" />
          <div className="text-sm text-blue-700">
            <strong>About:</strong> The scheduler automatically imports the previous day's data daily at your configured time. 
            Use the "Configure" button above to change the schedule, timezone, or enable/disable automation.
            Changes take effect immediately without requiring a server restart.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulerManager;